/**
 * 서/혼 훈련·연구 진행도 로컬 저장소 (MVP).
 *
 * <p>2026-04-23: 백엔드 스키마 변경 최소화를 위해 런 진행도를 localStorage 에 저장.
 * 향후 백엔드 `CharacterRun.trainingProgress` 필드로 마이그레이션 예정.
 *
 * <p>구조:
 * <pre>
 * {
 *   "{runId}": {
 *     "seo.gyeoldan": 5,   // 누적 훈련/연구 수행 회수
 *     "hon.hwayeom": 3,
 *     ...
 *   }
 * }
 * </pre>
 */

import { LEVEL_CURVE_CUMULATIVE, MASTERY_LEVEL, MASTERY_TOTAL_ACTIONS } from "@/utils/skillLabels";

const STORAGE_KEY = "spirytale.trainingProgress.v1";

type RunProgressMap = Record<string, number>; // pathId → actions
type AllRunsProgress = Record<string, RunProgressMap>; // runId → pathProgress

function loadAll(): AllRunsProgress {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return typeof parsed === "object" && parsed !== null ? parsed : {};
    } catch {
        return {};
    }
}

function saveAll(all: AllRunsProgress) {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    } catch { /* ignore */ }
}

/** 특정 런의 진행도 전체. 없으면 빈 객체. */
export function loadRunProgress(runId: string): RunProgressMap {
    const all = loadAll();
    return all[runId] ?? {};
}

/** 특정 path(서 또는 혼) 의 누적 회수. */
export function pathActions(runId: string, pathId: string): number {
    return loadRunProgress(runId)[pathId] ?? 0;
}

/**
 * 특정 path 의 Lv 계산. 0 = 선택 전 / 1~5.
 * 1 회 이상이면 Lv 1, 누적 {@link LEVEL_CURVE_CUMULATIVE}[i] 이상이면 Lv (i+1).
 */
export function pathLevel(runId: string, pathId: string): number {
    const actions = pathActions(runId, pathId);
    if (actions <= 0) return 0;
    let lv = 1;
    for (let i = 1; i < LEVEL_CURVE_CUMULATIVE.length; i += 1) {
        if (actions >= LEVEL_CURVE_CUMULATIVE[i]) lv = i + 1;
        else break;
    }
    return lv;
}

/** 다음 Lv 까지 남은 회수. 이미 마스터면 0. */
export function actionsToNextLevel(runId: string, pathId: string): number {
    const lv = pathLevel(runId, pathId);
    if (lv >= MASTERY_LEVEL) return 0;
    return LEVEL_CURVE_CUMULATIVE[lv] - pathActions(runId, pathId);
}

/** 진행도 증가. amount 기본 1. 증가 결과 Lv 반환 (Lv up 감지용). */
export function incrementPath(runId: string, pathId: string, amount: number = 1): { prevLevel: number; newLevel: number; actions: number } {
    const all = loadAll();
    const runMap = all[runId] ?? {};
    const prev = runMap[pathId] ?? 0;
    const next = Math.max(0, prev + amount);
    const prevLv = levelOfActions(prev);
    const newLv = levelOfActions(next);
    runMap[pathId] = next;
    all[runId] = runMap;
    saveAll(all);
    return { prevLevel: prevLv, newLevel: newLv, actions: next };
}

/** 특정 path 누적 회수 강제 설정 (밸런스 테스트 용). */
export function setPathActions(runId: string, pathId: string, actions: number) {
    const all = loadAll();
    const runMap = all[runId] ?? {};
    runMap[pathId] = Math.max(0, actions);
    all[runId] = runMap;
    saveAll(all);
}

function levelOfActions(actions: number): number {
    if (actions <= 0) return 0;
    let lv = 1;
    for (let i = 1; i < LEVEL_CURVE_CUMULATIVE.length; i += 1) {
        if (actions >= LEVEL_CURVE_CUMULATIVE[i]) lv = i + 1;
        else break;
    }
    return lv;
}

/** 특정 런의 진행도 리셋. */
export function resetRunProgress(runId: string) {
    const all = loadAll();
    delete all[runId];
    saveAll(all);
}

/** 모든 런 진행도. 디버그 뷰·Admin 용. */
export function loadAllProgress(): AllRunsProgress {
    return loadAll();
}

/** 특정 런·path 가 마스터 달성 이벤트를 이미 소비했는지. (중복 트리거 방지) */
const MASTERY_CONSUMED_KEY = "spirytale.masteryConsumed.v1";

function loadConsumed(): Record<string, string[]> {
    try {
        const raw = window.localStorage.getItem(MASTERY_CONSUMED_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return typeof parsed === "object" && parsed !== null ? parsed : {};
    } catch {
        return {};
    }
}

function saveConsumed(m: Record<string, string[]>) {
    try {
        window.localStorage.setItem(MASTERY_CONSUMED_KEY, JSON.stringify(m));
    } catch { /* ignore */ }
}

export function isMasteryConsumed(runId: string, pathId: string): boolean {
    const m = loadConsumed();
    return (m[runId] ?? []).includes(pathId);
}

export function markMasteryConsumed(runId: string, pathId: string) {
    const m = loadConsumed();
    const list = m[runId] ?? [];
    if (!list.includes(pathId)) {
        list.push(pathId);
        m[runId] = list;
        saveConsumed(m);
    }
}

/** 마스터 달성 총 회수 상수 재-export (호출부 편의). */
export { MASTERY_TOTAL_ACTIONS, MASTERY_LEVEL };

// ═════════════════════════════════════════════════════════════════════════════
// 현재 포커스된 훈련/연구 계열 (WeeklyTurn 액션 → 자동 증가 대상)
// ═════════════════════════════════════════════════════════════════════════════
//
// 사용자가 "이 서를 훈련한다" 로 지정. 이후 WeeklyTurn 에서 TRAINING/RESEARCH
// 액션 확정 시 해당 포커스 path 에 자동 +1. 미지정 시 null — 자동 증가 안 함.

const FOCUS_KEY = "spirytale.trainingFocus.v1";

type FocusKind = "seo" | "hon";
type RunFocusMap = Record<string, Partial<Record<FocusKind, string>>>;

function loadFocusMap(): RunFocusMap {
    try {
        const raw = window.localStorage.getItem(FOCUS_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return typeof parsed === "object" && parsed !== null ? parsed : {};
    } catch {
        return {};
    }
}

function saveFocusMap(m: RunFocusMap) {
    try {
        window.localStorage.setItem(FOCUS_KEY, JSON.stringify(m));
    } catch { /* ignore */ }
}

/** 현재 포커스된 서/혼 id. 없으면 null. */
export function getFocusedPath(runId: string, kind: FocusKind): string | null {
    const all = loadFocusMap();
    return all[runId]?.[kind] ?? null;
}

/** 포커스 설정. null 로 해제 가능. */
export function setFocusedPath(runId: string, kind: FocusKind, pathId: string | null) {
    const all = loadFocusMap();
    const run = all[runId] ?? {};
    if (pathId === null) {
        delete run[kind];
    } else {
        run[kind] = pathId;
    }
    all[runId] = run;
    saveFocusMap(all);
}
